import React, { FC, RefObject } from 'react';
import { SortingArrowIcon, TableButtonsIcon } from '@src/svg';
import CustomDropdown from '@uikit/CustomDropdown/CustomDropdown';
import { ControlledCheckbox } from '@uikit/FormItems/ControlledItems';
import { Column } from '@uikit/Tables/types';
import { useColumnDrag } from '@hooks/useColumnDrag';

const LOCKED_FIELDS = [
  'edit',
  'audit',
  'view',
  'viewImage',
  'checkboxes',
  'buttons',
];

interface TableHeaderProps {
  columnOrder: Column[];
  setColumnOrder: (cols: Column[]) => void;
  columns: Column[];
  control: any;
  tableRef: RefObject<HTMLTableElement>;
  hoveredCol: string | null;
  sortingTable?: { sortBy?: string; sortDir?: string };
  method?: string;
  setHoveredCol: (col: string | null) => void;
  onSorting: (col: Column) => void;
}

const TableHeader: FC<TableHeaderProps> = ({
  columnOrder,
  setColumnOrder,
  columns,
  control,
  tableRef,
  hoveredCol,
  sortingTable,
  method,
  setHoveredCol,
  onSorting,
}) => {
  const { getHeaderDragProps } = useColumnDrag(
    columnOrder,
    setColumnOrder,
    tableRef
  );

  const headerDropDownOptions = () =>
    columns
      .filter((el) => el.headerName !== 'checkboxes')
      .map((el) => ({
        item: (
          <div>
            <ControlledCheckbox
              name={el.field}
              title={el.headerName}
              control={control}
              dataTestId={`dynamic-added-item-${el.headerName}`}
            />
          </div>
        ),
        closeWhenClicked: false,
        onSelect: () => {},
      }));

  return (
    <thead className="thead-title">
      <tr className="t-row">
        {columnOrder.map((col, index) => {
          const isSorted = sortingTable?.sortBy === col.sortingField;
          const isNonEditable = LOCKED_FIELDS.includes(col.field);
          const dragProps = getHeaderDragProps(col, index);

          return (
            <th
              key={col.field}
              className={`sortable-header ${col.className ?? ''}`}
              onClick={() => onSorting(col)}
              onMouseEnter={() => setHoveredCol(col.field)}
              onMouseLeave={() => setHoveredCol(null)}
              {...dragProps}
            >
              <span className="table-header-content">
                {col.headerName !== 'checkboxes' ? (
                  <>
                    <span className="table-header-label">
                      {col.headerName}
                    </span>
                    {col.isSortable &&
                      method !== 'GET' &&
                      !isNonEditable &&
                      (isSorted || hoveredCol === col.field) && (
                        <span className="sorting-arrow-icon">
                          <SortingArrowIcon
                            width={16}
                            height={16}
                            isAsc={
                              isSorted
                                ? sortingTable?.sortDir === 'asc'
                                  ? 'asc'
                                  : 'desc'
                                : 'asc'
                            }
                          />
                        </span>
                      )}
                  </>
                ) : (
                  <CustomDropdown
                    button={<TableButtonsIcon />}
                    buttonClassName="table-buttons-trigger"
                    dropdownClassName="table_checkbox_options"
                    options={headerDropDownOptions()}
                  />
                )}
              </span>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default TableHeader;
